import clsx from "clsx";
import PropTypes from "prop-types";
import * as React from "react";
import { Menu as BaseMenu } from "@base-ui/react/menu";
import {
  RadioDotIcon,
  SubArrowIcon,
  TickIcon,
} from "../../primitives/Icons/Icons";
import css from "./Menu.module.css";

const MenuRoot = ({
  open,
  defaultOpen,
  onOpenChange,
  onOpenChangeComplete,
  modal = true,
  children,
  ...rest
}) => (
  <BaseMenu.Root
    open={open}
    defaultOpen={defaultOpen}
    onOpenChange={onOpenChange}
    onOpenChangeComplete={onOpenChangeComplete}
    modal={modal}
    {...rest}
  >
    {children}
  </BaseMenu.Root>
);

MenuRoot.displayName = "MenuRoot";

MenuRoot.propTypes = {
  open: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  onOpenChange: PropTypes.func,
  onOpenChangeComplete: PropTypes.func,
  modal: PropTypes.bool,
  children: PropTypes.node,
};

const MenuTrigger = React.forwardRef(
  ({ className, children, ...rest }, ref) => (
    <BaseMenu.Trigger ref={ref} className={className} {...rest}>
      {children}
    </BaseMenu.Trigger>
  ),
);

MenuTrigger.displayName = "MenuTrigger";

MenuTrigger.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const MenuPopup = React.forwardRef(
  (
    {
      side = "bottom",
      sideOffset = 4,
      align = "start",
      className,
      children,
      ...rest
    },
    ref,
  ) => (
    <BaseMenu.Portal>
      <BaseMenu.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        className={css.positioner}
      >
        <BaseMenu.Popup
          ref={ref}
          className={clsx(css.popup, className)}
          {...rest}
        >
          {children}
        </BaseMenu.Popup>
      </BaseMenu.Positioner>
    </BaseMenu.Portal>
  ),
);

MenuPopup.displayName = "MenuPopup";

MenuPopup.propTypes = {
  side: PropTypes.oneOf(["top", "bottom", "left", "right"]),
  sideOffset: PropTypes.number,
  align: PropTypes.oneOf(["start", "center", "end"]),
  className: PropTypes.string,
  children: PropTypes.node,
};

const MenuItem = React.forwardRef(
  (
    {
      disabled = false,
      label,
      closeOnClick = true,
      leading,
      trailing,
      className,
      children,
      ...rest
    },
    ref,
  ) => (
    <BaseMenu.Item
      ref={ref}
      disabled={disabled}
      label={label}
      closeOnClick={closeOnClick}
      className={clsx(css.item, className)}
      {...rest}
    >
      {leading && <span className={css.leading}>{leading}</span>}
      {children}
      {trailing && <span className={css.trailing}>{trailing}</span>}
    </BaseMenu.Item>
  ),
);

MenuItem.displayName = "MenuItem";

MenuItem.propTypes = {
  disabled: PropTypes.bool,
  label: PropTypes.string,
  closeOnClick: PropTypes.bool,
  leading: PropTypes.node,
  trailing: PropTypes.node,
  className: PropTypes.string,
  children: PropTypes.node,
};

const MenuCheckboxItem = React.forwardRef(
  (
    {
      checked,
      defaultChecked,
      onCheckedChange,
      disabled = false,
      label,
      closeOnClick = false,
      className,
      children,
      ...rest
    },
    ref,
  ) => (
    <BaseMenu.CheckboxItem
      ref={ref}
      checked={checked}
      defaultChecked={defaultChecked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      label={label}
      closeOnClick={closeOnClick}
      className={clsx(css.item, className)}
      {...rest}
    >
      <BaseMenu.CheckboxItemIndicator className={css.indicator}>
        <TickIcon />
      </BaseMenu.CheckboxItemIndicator>
      {children}
    </BaseMenu.CheckboxItem>
  ),
);

MenuCheckboxItem.displayName = "MenuCheckboxItem";

MenuCheckboxItem.propTypes = {
  checked: PropTypes.bool,
  defaultChecked: PropTypes.bool,
  onCheckedChange: PropTypes.func,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  closeOnClick: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
};

const MenuRadioGroup = ({ value, defaultValue, onValueChange, children }) => (
  <BaseMenu.RadioGroup
    value={value}
    defaultValue={defaultValue}
    onValueChange={onValueChange}
  >
    {children}
  </BaseMenu.RadioGroup>
);

MenuRadioGroup.displayName = "MenuRadioGroup";

MenuRadioGroup.propTypes = {
  value: PropTypes.any,
  defaultValue: PropTypes.any,
  onValueChange: PropTypes.func,
  children: PropTypes.node,
};

const MenuRadioItem = React.forwardRef(
  (
    {
      value,
      disabled = false,
      label,
      closeOnClick = false,
      className,
      children,
      ...rest
    },
    ref,
  ) => (
    <BaseMenu.RadioItem
      ref={ref}
      value={value}
      disabled={disabled}
      label={label}
      closeOnClick={closeOnClick}
      className={clsx(css.item, className)}
      {...rest}
    >
      <BaseMenu.RadioItemIndicator className={css.indicator}>
        <RadioDotIcon />
      </BaseMenu.RadioItemIndicator>
      {children}
    </BaseMenu.RadioItem>
  ),
);

MenuRadioItem.displayName = "MenuRadioItem";

MenuRadioItem.propTypes = {
  value: PropTypes.any.isRequired,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  closeOnClick: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
};

const MenuGroup = React.forwardRef(({ className, children, ...rest }, ref) => (
  <BaseMenu.Group ref={ref} className={className} {...rest}>
    {children}
  </BaseMenu.Group>
));

MenuGroup.displayName = "MenuGroup";

MenuGroup.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const MenuGroupLabel = React.forwardRef(
  ({ className, children, ...rest }, ref) => (
    <BaseMenu.GroupLabel
      ref={ref}
      className={clsx(css.groupLabel, className)}
      {...rest}
    >
      {children}
    </BaseMenu.GroupLabel>
  ),
);

MenuGroupLabel.displayName = "MenuGroupLabel";

MenuGroupLabel.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const MenuSeparator = React.forwardRef(({ className, ...rest }, ref) => (
  <BaseMenu.Separator
    ref={ref}
    className={clsx(css.separator, className)}
    {...rest}
  />
));

MenuSeparator.displayName = "MenuSeparator";

MenuSeparator.propTypes = {
  className: PropTypes.string,
};

const MenuSubmenuRoot = ({
  open,
  defaultOpen,
  onOpenChange,
  onOpenChangeComplete,
  children,
  ...rest
}) => (
  <BaseMenu.Root
    open={open}
    defaultOpen={defaultOpen}
    onOpenChange={onOpenChange}
    onOpenChangeComplete={onOpenChangeComplete}
    {...rest}
  >
    {children}
  </BaseMenu.Root>
);

MenuSubmenuRoot.displayName = "MenuSubmenuRoot";

MenuSubmenuRoot.propTypes = {
  open: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  onOpenChange: PropTypes.func,
  onOpenChangeComplete: PropTypes.func,
  children: PropTypes.node,
};

const MenuSubmenuTrigger = React.forwardRef(
  ({ className, children, ...rest }, ref) => (
    <BaseMenu.SubmenuTrigger
      ref={ref}
      className={clsx(css.item, className)}
      {...rest}
    >
      {children}
      <SubArrowIcon />
    </BaseMenu.SubmenuTrigger>
  ),
);

MenuSubmenuTrigger.displayName = "MenuSubmenuTrigger";

MenuSubmenuTrigger.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const MenuArrow = React.forwardRef(({ className, ...rest }, ref) => (
  <BaseMenu.Arrow ref={ref} className={clsx(css.arrow, className)} {...rest} />
));

MenuArrow.displayName = "MenuArrow";

MenuArrow.propTypes = {
  className: PropTypes.string,
};

const Menu = {
  Root: MenuRoot,
  Trigger: MenuTrigger,
  Popup: MenuPopup,
  Item: MenuItem,
  CheckboxItem: MenuCheckboxItem,
  RadioGroup: MenuRadioGroup,
  RadioItem: MenuRadioItem,
  Group: MenuGroup,
  GroupLabel: MenuGroupLabel,
  Separator: MenuSeparator,
  SubmenuRoot: MenuSubmenuRoot,
  SubmenuTrigger: MenuSubmenuTrigger,
  Arrow: MenuArrow,
};

export default Menu;
